import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lock } from "lucide-react";

const SESSION_KEY = "admin_unlocked";

function istHHMM(date) {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Kolkata",
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
  }).formatToParts(date);
  let hour = parts.find((p) => p.type === "hour").value;
  const minute = parts.find((p) => p.type === "minute").value;
  if (hour === "24") hour = "00"; // Intl hour12:false midnight quirk
  return hour + minute;
}

function expectedCode(date) {
  return istHHMM(date)
    .split("")
    .map((d) => (Number(d) + 1) % 10)
    .join("");
}

// Accept the current minute and the previous minute, to avoid a boundary
// race between showing the code and the user typing it in.
function validCodes() {
  const now = new Date();
  const oneMinAgo = new Date(now.getTime() - 60 * 1000);
  return [expectedCode(now), expectedCode(oneMinAgo)];
}

export default function AdminGate({ children }) {
  const [unlocked, setUnlocked] = useState(() => sessionStorage.getItem(SESSION_KEY) === "1");
  const [code, setCode] = useState("");
  const [error, setError] = useState(false);
  const rightCtrlDown = useRef(false);

  // Best-effort deterrents only — page JS cannot actually stop a browser's
  // native devtools; a determined user opens them via the browser menu
  // regardless. This blocks casual right-click/F12/devtools-shortcut access
  // while on /admin, and leaves Alt+I unblocked (note: Alt+I isn't a real
  // devtools shortcut in any mainstream browser, so it doesn't open anything
  // — it's just excluded from the blocked-combo list per request).
  useEffect(() => {
    const blockContextMenu = (e) => e.preventDefault();

    const blockDevtoolsKeys = (e) => {
      if (e.code === "ControlRight") rightCtrlDown.current = true;

      // Admin bypass: Alt + Right Ctrl + A, unlocks without the passcode.
      if (e.altKey && rightCtrlDown.current && e.key.toLowerCase() === "a") {
        e.preventDefault();
        sessionStorage.setItem(SESSION_KEY, "1");
        setUnlocked(true);
        return;
      }

      const key = e.key.toLowerCase();
      if (e.altKey && key === "i") return; // explicitly excluded from blocking

      const isF12 = e.key === "F12";
      const isDevtoolsCombo = (e.ctrlKey || e.metaKey) && e.shiftKey && ["i", "j", "c"].includes(key);
      const isViewSource = (e.ctrlKey || e.metaKey) && key === "u";

      if (isF12 || isDevtoolsCombo || isViewSource) {
        e.preventDefault();
      }
    };

    const blockKeyUp = (e) => {
      if (e.code === "ControlRight") rightCtrlDown.current = false;
    };

    window.addEventListener("contextmenu", blockContextMenu);
    window.addEventListener("keydown", blockDevtoolsKeys);
    window.addEventListener("keyup", blockKeyUp);
    return () => {
      window.removeEventListener("contextmenu", blockContextMenu);
      window.removeEventListener("keydown", blockDevtoolsKeys);
      window.removeEventListener("keyup", blockKeyUp);
    };
  }, []);

  if (unlocked) return children;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validCodes().includes(code.trim())) {
      sessionStorage.setItem(SESSION_KEY, "1");
      setUnlocked(true);
    } else {
      setError(true);
      setCode("");
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6">
      <form onSubmit={handleSubmit} className="swiss-card p-8 max-w-sm w-full" data-testid="admin-gate-form">
        <div className="w-12 h-12 bg-[var(--brand)] flex items-center justify-center mb-6">
          <Lock className="w-6 h-6 text-white" />
        </div>
        <h1 className="text-2xl font-black tracking-tight text-[#0A0A0A] mb-2">Admin Access</h1>
        <p className="text-sm text-[#525252] mb-6">Enter today's passcode to continue.</p>

        <Label className="text-xs font-semibold uppercase tracking-wider text-[#525252] mb-2 block">
          Passcode
        </Label>
        <Input
          type="password"
          inputMode="numeric"
          autoFocus
          value={code}
          onChange={(e) => {
            setCode(e.target.value);
            setError(false);
          }}
          className="rounded-none border-black/20 mb-2"
          data-testid="admin-gate-input"
        />
        {error && (
          <p className="text-sm text-red-600 mb-2" data-testid="admin-gate-error">
            Incorrect passcode.
          </p>
        )}

        <Button type="submit" className="w-full h-12 btn-brand rounded-none text-sm mt-4" data-testid="admin-gate-submit">
          Unlock
        </Button>
      </form>
    </div>
  );
}
